export type ReleaseNoteTag = "new" | "improved" | "fixed" | "breaking";

export type ReleaseNoteSection = {
  id: string;
  heading: string;
  tag: ReleaseNoteTag;
  bullets: string[];
};

export type ReleaseNoteFrontmatter = {
  title?: string;
  description?: string;
  lastUpdated?: string;
  releaseVersion?: string;
  appPage?: string;
};

export type ReleaseNoteListEntry = {
  slug: string;
  version: string;
  title: string;
  description: string;
  displayDate: string;
  lastUpdated: string;
  year: number;
};

export function headingToId(heading: string) {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export function inferTag(heading: string): ReleaseNoteTag {
  const h = heading.toLowerCase();
  if (/\bfix(es|ed)?\b|\bbug\b|\bpatch\b|\bresolv/.test(h)) return "fixed";
  if (/\bbreak|\bdeprecate|\bremov|\bdrop/.test(h)) return "breaking";
  if (/\bupdate|\bupgrad|\bimprov|\benhanc|\bexpand|\brefin|\bextend/.test(h))
    return "improved";
  return "new";
}

export function parseReleaseNoteSections(body: string): ReleaseNoteSection[] {
  const lines = body.split("\n");
  const sections: ReleaseNoteSection[] = [];
  let current: ReleaseNoteSection | null = null;

  for (const line of lines) {
    if (line.startsWith("### ")) {
      if (current) sections.push(current);
      const heading = line.slice(4).trim();
      current = {
        id: headingToId(heading),
        heading,
        tag: inferTag(heading),
        bullets: [],
      };
    } else if (line.startsWith("- ") && current) {
      current.bullets.push(line.slice(2).trim());
    } else if (line.trim() && current && !line.startsWith("#")) {
      current.bullets.push(line.trim());
    }
  }

  if (current) sections.push(current);
  return sections;
}

export function semverCompareDesc(a: string, b: string): number {
  const [ma, na] = a.split(".").map(Number);
  const [mb, nb] = b.split(".").map(Number);
  return mb !== ma ? mb - ma : nb - na;
}

export function manifestDateValue(dateStr: string): number {
  const [month, day, year] = String(dateStr).split("/").map(Number);
  return new Date(year, month - 1, day).getTime();
}

export function formatManifestDate(dateStr: string): string {
  const [m, d, y] = dateStr.split("/");
  return new Date(`${y}-${m}-${d}T12:00:00`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function formatIsoDate(dateStr: string): string {
  const date = new Date(`${dateStr}T12:00:00`);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}
